import { useEffect, useMemo } from "react";

type Alternate = {
  /** hreflang value, e.g. "en", "es", "pt", or "x-default" */
  hreflang: string;
  /** Absolute URL for that locale */
  href: string;
};

const SITE_ORIGIN = "https://www.switzh.com";
const DEFAULT_OG_IMAGE =
  "https://www.switzh.com/og-social-share.jpg";

/**
 * The canonical public host. Any other host serving this app (e.g. the
 * legacy `prompt-build-section.lovable.app` staging URL or a Lovable
 * preview) is considered non-canonical and should be flagged `noindex`
 * so search engines don't index duplicate URLs.
 */
const CANONICAL_HOSTS = new Set(["www.switzh.com", "switzh.com"]);

/** Per-locale og:image variants (Spanish and Portuguese). Falls back to the
 * English image when no entry exists for a given locale. */
const OG_IMAGE_BY_LANG: Record<string, string> = {
  en: "https://www.switzh.com/og-social-share.jpg",
  es: "https://www.switzh.com/og-social-share-es.jpg",
  pt: "https://www.switzh.com/og-social-share-pt.jpg",
};

const OG_LOCALE: Record<string, string> = {
  en: "en_US",
  es: "es_LA",
  pt: "pt_BR",
};

/**
 * Build hreflang alternates for an internal path across en/es/pt.
 * `path` should start with "/" (e.g. "/privacy", "/terms"). The English
 * version is used as x-default.
 */
export const buildLocaleAlternates = (path: string): Alternate[] => {
  const clean = path.startsWith("/") ? path : `/${path}`;
  const isHome = clean === "/";
  const en = isHome ? `${SITE_ORIGIN}/` : `${SITE_ORIGIN}${clean}`;
  const es = isHome ? `${SITE_ORIGIN}/es/` : `${SITE_ORIGIN}/es${clean}`;
  const pt = isHome ? `${SITE_ORIGIN}/pt/` : `${SITE_ORIGIN}/pt${clean}`;
  return [
    { hreflang: "en", href: en },
    { hreflang: "es", href: es },
    { hreflang: "pt", href: pt },
    { hreflang: "x-default", href: en },
  ];
};

/** Stable-reference version of buildLocaleAlternates for use inside components. */
export const useLocaleAlternates = (path: string): Alternate[] =>
  useMemo(() => buildLocaleAlternates(path), [path]);

type SeoOptions = {
  title: string;
  description?: string;
  canonical?: string;
  /** Alternate language URLs for hreflang. Include "x-default" for the fallback. */
  alternates?: Alternate[];
  /** Current language code ("en" | "es" | "pt"). Used for og:locale and og:locale:alternate. */
  lang?: string;
  /** Absolute URL for og:image / twitter:image. Defaults to the site's social image. */
  image?: string;
  /** og:type — defaults to "website". */
  ogType?: string;
};

const MANAGED_ATTR = "data-managed-seo";

/** Set or create a meta tag identified by an attribute key/value. Marks it as managed. */
const upsertMeta = (
  attrKey: "name" | "property",
  attrValue: string,
  content: string,
) => {
  let tag = document.head.querySelector(`meta[${attrKey}="${attrValue}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attrKey, attrValue);
    tag.setAttribute(MANAGED_ATTR, "true");
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
};

/**
 * Lightweight client-side SEO hook for SPA route changes.
 * Updates <title>, meta description, canonical link, hreflang alternates,
 * and Open Graph / Twitter Card tags.
 */
export const useSeo = ({
  title,
  description,
  canonical,
  alternates,
  lang,
  image,
  ogType = "website",
}: SeoOptions) => {
  useEffect(() => {
    if (title) document.title = title;

    // Force noindex on any non-canonical host (e.g. legacy lovable.app
    // staging URL, preview environments) so search engines only index
    // the production switzh.com domain.
    if (typeof window !== "undefined") {
      const host = window.location.hostname;
      const isCanonical = CANONICAL_HOSTS.has(host);
      let robots = document.querySelector('meta[name="robots"]');
      if (!isCanonical) {
        if (!robots) {
          robots = document.createElement("meta");
          robots.setAttribute("name", "robots");
          document.head.appendChild(robots);
        }
        robots.setAttribute("content", "noindex, nofollow");
        robots.setAttribute(MANAGED_ATTR, "true");
      } else if (
        robots &&
        robots.getAttribute(MANAGED_ATTR) === "true"
      ) {
        // We previously injected noindex for a non-canonical host; restore
        // the index-friendly default for the canonical host.
        robots.setAttribute(
          "content",
          "index, follow, max-image-preview:large",
        );
      }
    }

    if (description) {
      let tag = document.querySelector('meta[name="description"]');
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", "description");
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", description);
    }

    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonical);
    }

    // ── Open Graph + Twitter Card ──────────────────────────────────────
    const ogImage =
      image || (lang && OG_IMAGE_BY_LANG[lang]) || DEFAULT_OG_IMAGE;
    if (title) {
      upsertMeta("property", "og:title", title);
      upsertMeta("name", "twitter:title", title);
    }
    if (description) {
      upsertMeta("property", "og:description", description);
      upsertMeta("name", "twitter:description", description);
    }
    if (canonical) upsertMeta("property", "og:url", canonical);
    upsertMeta("property", "og:type", ogType);
    upsertMeta("property", "og:image", ogImage);
    upsertMeta("name", "twitter:image", ogImage);
    upsertMeta("name", "twitter:card", "summary_large_image");

    if (lang) {
      const ogLocale = OG_LOCALE[lang] || OG_LOCALE.en;
      upsertMeta("property", "og:locale", ogLocale);

      // Reset alternate locales managed by us, then add the other languages.
      document
        .querySelectorAll(
          `meta[property="og:locale:alternate"][${MANAGED_ATTR}="true"]`,
        )
        .forEach((el) => el.parentNode?.removeChild(el));

      Object.entries(OG_LOCALE)
        .filter(([code]) => code !== lang)
        .forEach(([, value]) => {
          const tag = document.createElement("meta");
          tag.setAttribute("property", "og:locale:alternate");
          tag.setAttribute("content", value);
          tag.setAttribute(MANAGED_ATTR, "true");
          document.head.appendChild(tag);
        });
    }

    // ── hreflang link alternates ───────────────────────────────────────
    if (alternates && alternates.length) {
      // Remove previously managed alternates so stale entries don't accumulate
      // when navigating between pages.
      document
        .querySelectorAll(`link[rel="alternate"][${MANAGED_ATTR}="true"]`)
        .forEach((el) => el.parentNode?.removeChild(el));

      for (const alt of alternates) {
        const link = document.createElement("link");
        link.setAttribute("rel", "alternate");
        link.setAttribute("hreflang", alt.hreflang);
        link.setAttribute("href", alt.href);
        link.setAttribute(MANAGED_ATTR, "true");
        document.head.appendChild(link);
      }

      return () => {
        document
          .querySelectorAll(`link[rel="alternate"][${MANAGED_ATTR}="true"]`)
          .forEach((el) => el.parentNode?.removeChild(el));
      };
    }
  }, [title, description, canonical, alternates, lang, image, ogType]);
};
